/**
 * Trade Logging Service
 * 
 * Centralized service for logging all user trades to the unified user_trades table.
 * This service is called by the trade engine to ensure all trades are tracked
 * regardless of the method used (manual, bots, etc.).
 */

import { supabase, adminSupabase } from './supabaseService';

export interface TradeLogEntry {
    // Required fields
    userWalletAddress: string;
    tradingWalletAddress: string;
    transactionSignature: string;
    tradeMethod: 'manual' | 'sniper_bot' | 'auto_trader' | 'copy_trader' | 'token_card' | 'quick_sell';
    tradeType: 'buy' | 'sell';
    userId?: string; // Optional user ID to avoid database lookup

    // Token information
    tokenAddress: string;
    tokenSymbol?: string;
    tokenName?: string;

    // Trade amounts
    inputMint: string; // SOL mint or token mint
    outputMint: string; // SOL mint or token mint
    inputAmount: string; // Amount as string to preserve precision
    outputAmount?: string; // Amount received (may be null for pending trades)
    solAmount: string; // SOL amount involved

    // Market data at execution time (CRUCIAL for analytics)
    tokenPriceUsd?: string; // Token price in USD at execution
    tokenPriceSol?: string; // Token price in SOL at execution
    marketCapUsd?: string; // Market cap in USD at execution
    marketCapSol?: string; // Market cap in SOL at execution
    liquidityUsd?: string; // Liquidity in USD at execution
    volume24hUsd?: string; // 24h volume in USD at execution

    // Trade execution details
    slippagePercent: string;
    priorityFeeLamports?: number;
    platformFeeSol?: string;
    networkFeeSol?: string;

    // Status
    status?: 'pending' | 'confirmed' | 'failed';
    errorMessage?: string;

    // Optional context
    ruleId?: string; // UUID of rule that triggered trade
    ruleName?: string;
    metadata?: Record<string, any>;
}

export interface TradeUpdateEntry {
    transactionSignature: string;
    status?: 'pending' | 'confirmed' | 'failed';
    outputAmount?: string;
    errorMessage?: string;
    metadata?: Record<string, any>;
}

// Helper function to safely parse numbers that might contain commas
function safeParseFloat(value: string | undefined | null): number | null {
    if (!value) return null;
    try {
        // Remove commas and parse
        const cleaned = value.toString().replace(/,/g, '');
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? null : parsed;
    } catch (error) {
        console.warn(`Failed to parse number: ${value}`, error);
        return null;
    }
}

// Helper function to safely clean string numbers
function safeCleanNumber(value: string | undefined | null): string | null {
    if (!value) return null;
    try {
        return value.toString().replace(/,/g, '');
    } catch (error) {
        console.warn(`Failed to clean number: ${value}`, error);
        return null;
    }
}

// Note: convertTokenAmount function removed - trade engine already provides human-readable amounts

class TradeLoggingService {
    /**
     * Log a new trade to the database
     */
    async logTrade(entry: TradeLogEntry): Promise<boolean> {
        try {
            console.log(`📝 Logging trade: ${entry.tradeType} ${entry.tokenSymbol || entry.tokenAddress} via ${entry.tradeMethod}`);
            console.log(`🔍 User context: ${entry.userId ? `Direct user ID: ${entry.userId}` : `Looking up by wallet: ${entry.userWalletAddress}`} (trading wallet: ${entry.tradingWalletAddress})`);

            let userId: string;

            if (entry.userId) {
                // Use provided user ID directly
                userId = entry.userId;
                console.log(`✅ Using provided user ID: ${userId}`);
            } else {
                // Fallback: look up user by wallet address using adminSupabase (bypasses RLS)
                console.log(`🔧 No user ID provided, looking up user for wallet: ${entry.userWalletAddress}`);

                const { data: userData, error: userError } = await adminSupabase
                    .from('users')
                    .select('id')
                    .eq('wallet_address', entry.userWalletAddress)
                    .single();

                if (userError || !userData) {
                    console.error('❌ Failed to find user for trade logging:', userError);
                    console.error(`❌ Searched for wallet_address: ${entry.userWalletAddress}`);

                    // Try to create the user if not found
                    console.log(`🔧 User not found, creating new user for wallet: ${entry.userWalletAddress}`);

                    const newUserData = {
                        wallet_address: entry.userWalletAddress,
                        wallet_provider: 'unknown',
                        last_login: new Date().toISOString(),
                        login_count: 1,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString()
                    };

                    const { data: newUser, error: createError } = await adminSupabase
                        .from('users')
                        .insert([newUserData])
                        .select('id')
                        .single();

                    if (createError || !newUser) {
                        console.error('❌ Failed to create user for trade logging:', createError);
                        return false;
                    }

                    userId = newUser.id;
                    console.log(`✅ Created new user with ID: ${userId} for wallet: ${entry.userWalletAddress}`);
                } else {
                    userId = userData.id;
                    console.log(`✅ Found existing user with ID: ${userId} for wallet: ${entry.userWalletAddress}`);
                }
            }

            // Trade amounts are already in human-readable format from trade engine
            // No conversion needed - just clean and validate the numbers
            console.log(`📊 [DEBUG] Trade amounts received (already human-readable):`, {
                inputAmount: entry.inputAmount,
                outputAmount: entry.outputAmount,
                solAmount: entry.solAmount,
                inputMint: entry.inputMint,
                outputMint: entry.outputMint
            });

            // Prepare the trade data
            const tradeData = {
                user_id: userId,
                transaction_signature: entry.transactionSignature,
                trade_method: entry.tradeMethod,
                trade_type: entry.tradeType,

                // Token information
                token_address: entry.tokenAddress,
                token_symbol: entry.tokenSymbol || null,
                token_name: entry.tokenName || null,

                // Wallet information
                user_wallet_address: entry.userWalletAddress,
                trading_wallet_address: entry.tradingWalletAddress,

                // Trade amounts - already in human-readable format, just clean them
                input_mint: entry.inputMint,
                output_mint: entry.outputMint,
                input_amount: safeCleanNumber(entry.inputAmount) || '0',
                output_amount: safeCleanNumber(entry.outputAmount),
                sol_amount: safeCleanNumber(entry.solAmount) || '0',

                // Market data at execution time - safely parse comma-formatted numbers
                token_price_usd: safeParseFloat(entry.tokenPriceUsd),
                token_price_sol: safeParseFloat(entry.tokenPriceSol),
                market_cap_usd: safeParseFloat(entry.marketCapUsd),
                market_cap_sol: safeParseFloat(entry.marketCapSol),
                liquidity_usd: safeParseFloat(entry.liquidityUsd),
                volume_24h_usd: safeParseFloat(entry.volume24hUsd),

                // Trade execution details - safely parse comma-formatted numbers
                slippage_percent: safeParseFloat(entry.slippagePercent) || 0,
                priority_fee_lamports: entry.priorityFeeLamports || 0,
                platform_fee_sol: safeParseFloat(entry.platformFeeSol) || 0,
                network_fee_sol: safeParseFloat(entry.networkFeeSol) || 0.000005,

                // Status and context
                status: entry.status || 'pending',
                error_message: entry.errorMessage || null,
                rule_id: entry.ruleId || null,
                rule_name: entry.ruleName || null,
                metadata: entry.metadata || {}
            };

            // Insert the trade using adminSupabase to bypass RLS
            const { error: insertError } = await adminSupabase
                .from('user_trades')
                .insert(tradeData);

            if (insertError) {
                console.error('❌ Failed to log trade:', insertError);
                return false;
            }

            console.log(`✅ Trade logged successfully: ${entry.transactionSignature}`);
            return true;
        } catch (error) {
            console.error('❌ Error logging trade:', error);
            return false;
        }
    }

    /**
     * Update an existing trade (e.g., when status changes from pending to confirmed)
     */
    async updateTrade(update: TradeUpdateEntry): Promise<boolean> {
        try {
            console.log(`📝 Updating trade status: ${update.transactionSignature} -> ${update.status}`);

            const updateData: any = {};

            if (update.status) updateData.status = update.status;
            if (update.outputAmount) updateData.output_amount = update.outputAmount;
            if (update.errorMessage) updateData.error_message = update.errorMessage;
            if (update.metadata) updateData.metadata = update.metadata;

            const { error } = await adminSupabase
                .from('user_trades')
                .update(updateData)
                .eq('transaction_signature', update.transactionSignature);

            if (error) {
                console.error('❌ Failed to update trade:', error);
                return false;
            }

            console.log(`✅ Trade updated successfully: ${update.transactionSignature}`);
            return true;
        } catch (error) {
            console.error('❌ Error updating trade:', error);
            return false;
        }
    }

    /**
     * Get user's trade history
     */
    async getUserTrades(
        userWalletAddress: string,
        options: {
            limit?: number;
            offset?: number;
            tradeMethod?: string;
            tradeType?: 'buy' | 'sell';
            tokenAddress?: string;
            status?: 'pending' | 'confirmed' | 'failed';
        } = {}
    ) {
        try {
            // First get user_id using adminSupabase to bypass RLS
            const { data: userData, error: userError } = await adminSupabase
                .from('users')
                .select('id')
                .eq('wallet_address', userWalletAddress)
                .single();

            if (userError || !userData) {
                console.error('❌ Failed to find user for trade history:', userError);
                return { data: [], error: userError };
            }

            let query = adminSupabase
                .from('user_trades')
                .select('*')
                .eq('user_id', userData.id)
                .order('created_at', { ascending: false });

            // Apply filters
            if (options.tradeMethod) query = query.eq('trade_method', options.tradeMethod);
            if (options.tradeType) query = query.eq('trade_type', options.tradeType);
            if (options.tokenAddress) query = query.eq('token_address', options.tokenAddress);
            if (options.status) query = query.eq('status', options.status);

            // Apply pagination
            if (options.limit) query = query.limit(options.limit);
            if (options.offset) query = query.range(options.offset, (options.offset + (options.limit || 50)) - 1);

            const { data, error } = await query;

            return { data: data || [], error };
        } catch (error) {
            console.error('❌ Error fetching user trades:', error);
            return { data: [], error };
        }
    }

    /**
     * Get trade statistics for a user
     */
    async getUserTradeStats(userWalletAddress: string) {
        try {
            // First get user_id using adminSupabase to bypass RLS
            const { data: userData, error: userError } = await adminSupabase
                .from('users')
                .select('id')
                .eq('wallet_address', userWalletAddress)
                .single();

            if (userError || !userData) {
                return { data: null, error: userError };
            }

            // Get trade statistics
            const { data, error } = await adminSupabase
                .from('user_trades')
                .select('trade_type, trade_method, status, sol_amount, created_at')
                .eq('user_id', userData.id);

            if (error || !data) {
                return { data: null, error };
            }

            // Calculate statistics
            const stats = {
                totalTrades: data.length,
                totalBuys: data.filter((t: any) => t.trade_type === 'buy').length,
                totalSells: data.filter((t: any) => t.trade_type === 'sell').length,
                totalSolTraded: data.reduce((sum: number, t: any) => sum + parseFloat(t.sol_amount || '0'), 0),
                confirmedTrades: data.filter((t: any) => t.status === 'confirmed').length,
                failedTrades: data.filter((t: any) => t.status === 'failed').length,
                pendingTrades: data.filter((t: any) => t.status === 'pending').length,
                methodBreakdown: data.reduce((acc: Record<string, number>, t: any) => {
                    acc[t.trade_method] = (acc[t.trade_method] || 0) + 1;
                    return acc;
                }, {} as Record<string, number>),
                last30Days: data.filter((t: any) => {
                    const tradeDate = new Date(t.created_at);
                    const thirtyDaysAgo = new Date();
                    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                    return tradeDate >= thirtyDaysAgo;
                }).length
            };

            return { data: stats, error: null };
        } catch (error) {
            console.error('❌ Error fetching trade stats:', error);
            return { data: null, error };
        }
    }
}

// Export singleton instance
export const tradeLoggingService = new TradeLoggingService();

// Helper function to determine trade method from context
export function determineTradeMethod(context?: {
    isSniper?: boolean;
    isAutoTrader?: boolean;
    isCopyTrader?: boolean;
    isTokenCard?: boolean;
    isQuickSell?: boolean;
}): TradeLogEntry['tradeMethod'] {
    if (context?.isSniper) return 'sniper_bot';
    if (context?.isAutoTrader) return 'auto_trader';
    if (context?.isCopyTrader) return 'copy_trader';
    if (context?.isTokenCard) return 'token_card';
    if (context?.isQuickSell) return 'quick_sell';
    return 'manual';
}

// Helper function to get token info from mint address
export async function getTokenInfoForLogging(tokenMint: string): Promise<{
    symbol?: string;
    name?: string;
}> {
    try {
        // Try to get token info from our token registry or API
        try {
            const { getTokenInfoByMint } = await import('./tokenRegistry');
            const tokenInfo = await getTokenInfoByMint(tokenMint);

            if (tokenInfo) {
                return {
                    symbol: (tokenInfo as any).symbol,
                    name: (tokenInfo as any).name
                };
            }
        } catch (registryError) {
            console.warn('Token registry lookup failed, trying Solana Tracker API');
        }

        // Fallback: try Solana Tracker API with new structure
        const { getTokenInfo } = await import('./solanatrackerService');
        const response = await getTokenInfo(tokenMint);

        if (response.status === 'success' && response.data?.token) {
            return {
                symbol: response.data.token.symbol,
                name: response.data.token.name
            };
        }

        return {};
    } catch (error) {
        console.warn('Could not fetch token info for logging:', error);
        return {};
    }
}

// Helper function to get market data at execution time
export async function getMarketDataForLogging(tokenMint: string): Promise<{
    tokenPriceUsd?: string;
    tokenPriceSol?: string;
    marketCapUsd?: string;
    marketCapSol?: string;
    liquidityUsd?: string;
    volume24hUsd?: string;
}> {
    try {
        console.log(`📊 Fetching market data for token: ${tokenMint}`);

        // Use Solana Tracker API to get current market data
        const { getTokenInfo } = await import('./solanatrackerService');
        const response = await getTokenInfo(tokenMint);

        if (response.status === 'success' && response.data) {
            const data = response.data;

            // Extract data from the new API structure
            const token = data.token;
            const pool = data.pools?.[0]; // Get the first (usually main) pool

            if (!pool) {
                console.warn(`⚠️ No pool data available for token: ${tokenMint}`);
                return {};
            }

            // Extract market data with proper null handling from the new structure
            const marketData = {
                tokenPriceUsd: pool.price?.usd?.toString(),
                tokenPriceSol: pool.price?.quote?.toString(),
                marketCapUsd: pool.marketCap?.usd?.toString(),
                marketCapSol: pool.marketCap?.quote?.toString(),
                liquidityUsd: pool.liquidity?.usd?.toString(),
                volume24hUsd: pool.txns?.volume?.toString()
            };

            console.log(`✅ Market data fetched for ${token?.symbol || tokenMint}:`, {
                price: marketData.tokenPriceUsd ? `$${marketData.tokenPriceUsd}` : 'N/A',
                marketCap: marketData.marketCapUsd ? `$${marketData.marketCapUsd}` : 'N/A',
                liquidity: marketData.liquidityUsd ? `$${marketData.liquidityUsd}` : 'N/A'
            });

            return marketData;
        }

        console.warn(`⚠️ No market data available for token: ${tokenMint}`);
        return {};
    } catch (error) {
        console.error(`❌ Error fetching market data for token ${tokenMint}:`, error);
        return {};
    }
}

// Enhanced function to get both token info and market data
export async function getTokenAndMarketDataForLogging(tokenMint: string): Promise<{
    symbol?: string;
    name?: string;
    tokenPriceUsd?: string;
    tokenPriceSol?: string;
    marketCapUsd?: string;
    marketCapSol?: string;
    liquidityUsd?: string;
    volume24hUsd?: string;
}> {
    try {
        // Fetch both token info and market data in parallel for efficiency
        const [tokenInfo, marketData] = await Promise.all([
            getTokenInfoForLogging(tokenMint),
            getMarketDataForLogging(tokenMint)
        ]);

        return {
            ...tokenInfo,
            ...marketData
        };
    } catch (error) {
        console.error('Error fetching token and market data:', error);
        return {};
    }
} 